import type { ComplexFilterOutput } from '@/components/filters/ComplexFilter.vue';
import type { NestedGroupFilter } from '@/components/filters/ComplexFilterNestedGroup.vue';

import { ReportFilterGroup, ReportFilterGroupState } from '@/types/reports';

export function clearUserAddedFilters(
  groups: Record<string | number, ReportFilterGroupState>,
  labels: Set<string>,
) {
  for (const key of Object.keys(groups)) {
    const group = groups[key];
    if (!group?.filters) continue;
    group.filters = group.filters.filter(
      (f) => !f.label || !labels.has(f.label),
    );
    if (!group.filters.length) {
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete groups[key];
    }
  }
}

function rebuildExpression(
  originalExpression: string[],
  keptLabels: Set<string>,
): string[] {
  return originalExpression.reduce<string[]>((expr, token, i) => {
    if (i % 2 !== 0) return expr;
    if (!token || !keptLabels.has(token)) return expr;
    const precedingCombinator = originalExpression[i - 1];
    if (expr.length && precedingCombinator) expr.push(precedingCombinator);
    expr.push(token);
    return expr;
  }, []);
}

export function stripUserAddedFromFilters(
  filters: ReportFilterGroup[] | undefined,
  labels: Set<string>,
): ReportFilterGroup[] {
  if (!filters?.length) return filters ?? [];
  if (!labels.size) return filters;
  return filters.reduce<ReportFilterGroup[]>((acc, group) => {
    const keptParts = group.filter_parts.filter(
      (p) => !p.label || !labels.has(p.label),
    );
    if (!keptParts.length) return acc;
    const keptLabelSet = new Set(
      keptParts.map((p) => p.label).filter((l): l is string => Boolean(l)),
    );
    const originalExpression = (group.filter_expression as string[]) ?? [];
    acc.push({
      ...group,
      filter_parts: keptParts,
      filter_expression: rebuildExpression(originalExpression, keptLabelSet),
    });
    return acc;
  }, []);
}

export function stripUserAddedRowsFromState(
  state: ComplexFilterOutput | null,
  labels: Set<string>,
): ComplexFilterOutput | null {
  if (!state) return state;
  if (!labels.size) return state;
  function stripFilters(filters: NestedGroupFilter[]) {
    return filters.filter((f) => !f.label || !labels.has(f.label));
  }
  function stripGroup<
    G extends { filters: NestedGroupFilter[]; expression: string[] },
  >(g: G): G {
    const filters = stripFilters(g.filters);
    const keptLabels = new Set(
      filters.map((f) => f.label).filter((l): l is string => Boolean(l)),
    );
    return {
      ...g,
      filters,
      expression: rebuildExpression(g.expression, keptLabels),
    };
  }
  const partnerGroups = state.partnerGroups
    ?.map((p) => ({
      ...p,
      groups: p.groups.map(stripGroup).filter((g) => g.filters.length > 0),
    }))
    .filter((p) => p.groups.length > 0);
  return {
    ownGroups: state.ownGroups
      .map(stripGroup)
      .filter((g) => g.filters.length > 0),
    partnerGroups: partnerGroups?.length ? partnerGroups : undefined,
  };
}
